// React
import React from "react";
import { PortableText } from "@portabletext/react";
import { Link } from "react-router-dom"; // Use for internal links if needed

// This is the new, simplified component for rendering blog post content.

const BlogPostComponent = ({ body }) => {
  /**
   * This 'components' object tells the <PortableText> component how to render
   * specific nodes from your Sanity content. We can customize anything, from
   * block types (images) to marks (links) and styles (headings).
   */
  const ptComponents = {
    // Defines how to render custom block types
    types: {
      image: ({ value }) => {
        if (!value?.asset?.url) {
          return null;
        }
        return (
          <figure className="blog-post__body-image">
            <img src={value.asset.url} alt={value.alt || "Blog image"} />
            {value.caption && (
              <figcaption className="blog-post__image-caption">
                {value.caption}
              </figcaption>
            )}
          </figure>
        );
      },
      // You can add other custom types here, e.g., 'callToAction', 'videoEmbed'
    },

    // Defines how to render annotations on text (e.g., links, emphasis)
    marks: {
      link: ({ children, value }) => {
        const isExternal = value.href.startsWith("http");
        // Render external links with an `<a>` tag
        if (isExternal) {
          return (
            <a
              href={value.href}
              target="_blank"
              rel="noopener noreferrer"
              className="blog-post__link"
            >
              {children}
            </a>
          );
        }
        // Render internal links with React Router's <Link>
        return (
          <Link to={value.href} className="blog-post__link">
            {children}
          </Link>
        );
      },
    },

    // Defines how to render standard block styles (headings, paragraphs, etc.)
    block: {
      h1: ({ children }) => <h1 className="blog-post__h1">{children}</h1>,
      h2: ({ children }) => <h2 className="blog-post__h2">{children}</h2>,
      h3: ({ children }) => <h3 className="blog-post__h3">{children}</h3>,
      h4: ({ children }) => <h4 className="blog-post__h4">{children}</h4>,
      blockquote: ({ children }) => (
        <blockquote className="blog-post__blockquote">{children}</blockquote>
      ),
    },

    // Defines how to render lists
    list: {
      bullet: ({ children }) => <ul className="blog-post__ul">{children}</ul>,
      number: ({ children }) => <ol className="blog-post__ol">{children}</ol>,
    },

    // Defines how to render list items
    listItem: {
      bullet: ({ children }) => <li>{children}</li>,
      number: ({ children }) => <li>{children}</li>,
    },
  };

  return <PortableText value={body} components={ptComponents} />;
};

export default BlogPostComponent;
